import type { AccountPosition, AccountRetirement, LinkedBorrowingDebt, AccountState, AccountStatement, Currency, JournalEntry, LedgerAccount, Money } from "../../entities/ledger/types";
import { apiRequest } from "../../shared/api/client";
import { type OpenAccountForm } from "./model";

export interface AccountDetail {
  id: string;
  bookId: string;
  name: string;
  currency: Currency;
  reference?: string;
  displayValue?: string;
  displayLabel?: string;
  archived?: boolean;
  asOf: string;
  historySource: "journal" | "movements";
  nativeBalance: Money;
  carryingBookValue: Money;
  reportingValue?: Money | null;
  position?: AccountPosition;
  debtAttribution?: "borrowing" | "unattributed";
  linkedBorrowings?: LinkedBorrowingDebt[];
  state?: AccountState | null;
  importCoverage?: { latest?: { id: string; coversFrom: string; coversThrough: string; source?: string; recordedAt: string }; count: number } | null;
}

export function getAccountDetail(accountID: string, asOf?: string): Promise<AccountDetail> {
  const query = asOf ? `?asOf=${encodeURIComponent(asOf)}` : "";
  return apiRequest(`/v1/accounts/${encodeURIComponent(accountID)}${query}`);
}

export function openAccount(bookID: string, values: OpenAccountForm): Promise<{ account: LedgerAccount }> {
  const opening = values.openingAmount === "" || Number(values.openingAmount) === 0
    ? undefined
    : {
        amount: { amount: values.openingAmount, currency: values.currency },
        effectiveAt: new Date(values.effectiveAt).toISOString(),
        manualRate: values.manualRate || undefined,
      };
  return apiRequest(`/v1/books/${encodeURIComponent(bookID)}/accounts`, {
    method: "POST",
    idempotent: true,
    body: {
      name: values.name,
      currency: values.currency, reference: values.reference || undefined, opening,
    },
  });
}

// Presentation only: the account keeps its id, currency, type and postings.
export function updateAccount(
  _bookID: string,
  accountID: string,
  patch: { name?: string; reference?: string; displayValue?: string },
): Promise<{ account: LedgerAccount }> {
  return apiRequest(`/v1/accounts/${encodeURIComponent(accountID)}`, { method: "PATCH", body: patch });
}

export function getAccountStatement(
  bookID: string,
  accountID: string,
  from: string,
  to: string,
): Promise<AccountStatement> {
  const query = new URLSearchParams({ from, to });
  return apiRequest(`/v1/accounts/${encodeURIComponent(accountID)}/statement?${query}`);
}


export function currencyName(currency: Currency): string {
  const names: Record<Currency, string> = {
    GEL: "Georgian lari", USD: "US dollar", EUR: "Euro", GBP: "Pound sterling",
    TRY: "Turkish lira", RUB: "Russian ruble", JPY: "Japanese yen", KWD: "Kuwaiti dinar",
    UAH: "Ukrainian hryvnia", VND: "Vietnamese dong", PLN: "Polish zloty", CHF: "Swiss franc",
  };
  return names[currency];
}

export interface ReconciliationResult {
  account: LedgerAccount;
  calculatedBalance: Money;
  declaredBalance: Money;
  difference: Money;
  adjustment?: JournalEntry | null;
  created: boolean;
}

export function reconcileAccount(
  bookID: string,
  accountID: string,
  // balanceKind names which figure is declared. The server refuses the wrong
  // one rather than guessing, which is what keeps a card's debt from being
  // entered as its account balance.
  input: { declaredBalance: string; currency: Currency; effectiveAt: string; balanceKind?: "account" | "debt" },
): Promise<ReconciliationResult> {
  return apiRequest(`/v1/accounts/${encodeURIComponent(accountID)}/reconciliations`, {
    method: "POST",
    idempotent: true,
    body: {
      meta: { effectiveAt: input.effectiveAt, description: "" },
      balanceKind: input.balanceKind,
      declaredBalance: { amount: input.declaredBalance.trim(), currency: input.currency },
    },
  });
}

// Soft delete: archives (hides, closes to new postings) a balanced account.
// Nothing is removed; refused for system accounts and non-zero balances.
export function retireAccount(_bookID: string, accountID: string): Promise<AccountRetirement> {
  return apiRequest(`/v1/accounts/${encodeURIComponent(accountID)}`, { method: "DELETE" });
}

// An archived account is hidden from the ordinary listing, not gone. Asking
// for it is how a reader finds one to reopen.
export function listAccounts(bookID: string, includeArchived: boolean): Promise<{ accounts: LedgerAccount[] }> {
  const query = includeArchived ? "?includeArchived=true" : "";
  return apiRequest(`/v1/books/${encodeURIComponent(bookID)}/accounts${query}`);
}

// Archiving is a soft delete and keeps every posting, so reopening is
// symmetrical: the account comes back with its history and accepts entries
// again. Nothing about the journal changed while it was closed.
export function restoreAccount(bookID: string, accountID: string): Promise<AccountRetirement> {
  return apiRequest(
    `/v1/accounts/${encodeURIComponent(accountID)}/restore`,
    { method: "POST", body: {} },
  );
}

// What the institution reports, recorded without touching the journal. A
// credit limit and a spendable total are not movements of money, so this
// writes no posting and changes no total.
export function declareAccountState(
  bookID: string,
  accountID: string,
  body: {
    // availableFunds is the total spendable including unused credit. A bank
    // field merely labelled "balance" is not evidence enough to fill it.
    availableFunds?: { amount: string; currency: Currency };
    // AccountState only accepts endpoint availability. Facility limits and
    // reported debt are Borrowing observations, not Account state.
    clear?: string[];
    // externalId makes recording the same report twice one fact, not two;
    // supersedes names an earlier observation this one corrects.
    externalId?: string;
    supersedes?: string;
    observedAt?: string; source?: string; note?: string;
  },
): Promise<{ state: AccountState }> {
  return apiRequest(`/v1/accounts/${encodeURIComponent(accountID)}/state`,
    { method: "POST", body });
}
